import {createContext, useContext, useMemo, useReducer, useRef} from 'react';
import {useLocation, useNavigationType} from 'react-router-dom';

import {
  initialNavigationHistoryState,
  navigationHistoryReducer
} from './navigation-history.reducer';

import type {FC, ReactNode} from 'react';

export interface NavigationHistoryContextValue {
  canGoBack: boolean;
  canGoForward: boolean;
}

const NavigationHistoryContext = createContext<NavigationHistoryContextValue | null>(null);

export interface NavigationHistoryProviderProps {
  children: ReactNode;
}

export const NavigationHistoryProvider: FC<NavigationHistoryProviderProps> = ({children}) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [state, dispatch] = useReducer(
    navigationHistoryReducer,
    location.key,
    initialNavigationHistoryState
  );

  const processedKeyRef = useRef(location.key);
  if (processedKeyRef.current !== location.key) {
    processedKeyRef.current = location.key;
    dispatch({type: navigationType, key: location.key});
  }

  const canGoBack = state.index > 0;
  const canGoForward = state.index < state.stack.length - 1;
  const value = useMemo<NavigationHistoryContextValue>(
    () => ({canGoBack, canGoForward}),
    [canGoBack, canGoForward]
  );

  return (
    <NavigationHistoryContext.Provider value={value}>{children}</NavigationHistoryContext.Provider>
  );
};

export function useNavigationHistory(): NavigationHistoryContextValue {
  const context = useContext(NavigationHistoryContext);
  if (!context) {
    throw new Error('useNavigationHistory must be used within a NavigationHistoryProvider');
  }
  return context;
}
